import React from "react";
import update from "immutability-helper";
import _ from "lodash";
import moment from "moment";

import {
  WorksheetClipboardOutlined,
  StudentOutlined,
  SettingsOutlined,
  LockOpenOutlined,
  LockOutlined,
  PolicyOutlined,
  ClockAlarmOutlined,
  CircleFilled,
} from "@toddle-edu/ds-icons";

import { getCourseIdsMemoize } from "UnitPlans/components/IBcomponents/MaxScoreTool/Utils";
import {
  checkIsDateAfter,
  getTodaysDate,
  formatDate,
  checkIsDateSame,
  checkIsDateBefore,
} from "Utils/DateTimeHelpers";
import { generateRnPContextsByLevel } from "Utils/RnPHelpers";

import {
  ASSESSMENT_TYPE_AI_TUTOR,
  ASSESSMENT_TYPE_QUICK_TASK,
  OPENS_ON,
  DUE_ON,
  CLOSES_ON,
  VISIBILITY,
  STUDENTS,
  TAGS,
  SECURITY_MODE,
  DURATION,
  TO_BE_VISIBLE,
  VISIBLE,
  PRIVATE,
  TASK_TYPE_AI_TUTOR,
} from "Constants/stringConstants";
import {
  UNTIMED,
  TIMED,
  FIXED_TIMED,
  UNRESTRICTED,
  BROWSER_LOCK,
  LOCKDOWN_BROWSER,
  WORKSHEET_TEMPLATES,
} from "Worksheet/modules/WorksheetConstants";
import {
  getIsCurrentDateSelected,
  getScheduleOrDeadlineTime,
} from "ClassRoom/modules/utils";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import ACLStore from "lib/aclStore";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import { getIsAssignmentsUnitTaggingIn2_0Enabled } from "Utils/ACLStoreUtils";
import ACLStorage from "lib/aclService";
import {
  CAN_EDIT_COURSE_FLOW_PERMISSION,
  CLASS_FLOW_EDIT_PERMISSION,
} from "Constants/permissionConstants";
import { isToddleLibraryRouteBySegment as isToddleLibraryRoute } from "Utils/RouteMatchUtils";
import { getIsWorksheetAssignModalV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";

const DATE_FORMAT = "DD MMMM";
const TIME_FORMAT = "hh:mm A";

export const getSettingGroups = ({ t, isWorksheet }) => {
  const isWorksheetLayoutUpdate =
    isWorksheet && getIsWorksheetAssignModalV2Enabled();

  const SETTING_GROUPS = [
    {
      key: "WORKSHEET_SETTINGS",
      label: t("common:type"),
      icon: <WorksheetClipboardOutlined />,
    },
    {
      key: "CLASS_WISE_SETTINGS",
      label: isWorksheetLayoutUpdate
        ? t("classRoom:assign_settings")
        : t("common:class_plural"),
      icon: <StudentOutlined />,
    },
    {
      key: "COMMON_SETTINGS",
      label: isWorksheetLayoutUpdate
        ? t("common:other_settings")
        : t("common:settings"),
      icon: <SettingsOutlined />,
    },
  ];

  return _.filter(
    SETTING_GROUPS,
    ({ key }) => key !== "WORKSHEET_SETTINGS" || isWorksheet
  );
};

export const getAssignmentClassWiseSettings = _.memoize(
  ({ t, currentTimelineSettings, isFixedStartTime, isTimedTestEnabled }) => {
    const isWorksheetLayoutUpdateEnabled = getIsWorksheetAssignModalV2Enabled();

    const ASSIGNMENT_CLASS_WISE_SETTINGS = [
      {
        key: STUDENTS,
        label: t("classRoom:students"),
      },
      {
        key: SECURITY_MODE,
        label: t("worksheet:security_mode"),
        showForTimedWorksheetOnly: true,
      },
      {
        key: DURATION,
        label: t("common:duration"),
        showForTimedWorksheetOnly: true,
      },
      {
        key: OPENS_ON,
        label: isFixedStartTime
          ? t("worksheet:starts_on")
          : t("common:opens_on"),
        showBasedOnPreference: true,
      },
      {
        key: DUE_ON,
        label: t("classRoom:due_on"),
        showBasedOnPreference: true,
        hideForFixedTimeWorksheet: true,
      },
      {
        key: CLOSES_ON,
        label: t("common:closes_on"),
        showBasedOnPreference: true,
        hideForFixedTimeWorksheet: true,
      },
      {
        key: VISIBILITY,
        label: t("common:visibility"),
        showBasedOnPreference: true,
      },
      {
        key: TAGS,
        label: t("common:tags"),
      },
    ];

    return _.filter(
      ASSIGNMENT_CLASS_WISE_SETTINGS,
      ({
        key,
        showBasedOnPreference = false,
        hideForFixedTimeWorksheet = false,
        showForTimedWorksheetOnly = false,
      }) => {
        // Security Mode and Duration columns only show when feature flag is on AND worksheet is timed
        if (showForTimedWorksheetOnly) {
          return isWorksheetLayoutUpdateEnabled && isTimedTestEnabled;
        }
        if (isFixedStartTime) {
          // For isFixedStartTime we will hide DUE_ON and CLOSES_ON
          return !hideForFixedTimeWorksheet;
        }
        return (
          !showBasedOnPreference || _.includes(currentTimelineSettings, key)
        );
      }
    );
  },
  JSON.stringify
);

export const getAssignmentVisibilityOptions = _.memoize(
  ({ t, opensOn, isFixedStartTime }) => {
    const isOpenDateInFuture =
      !_.isEmpty(opensOn) &&
      checkIsDateAfter({
        date1: opensOn,
        date2: getTodaysDate(),
        key: "minutes",
      });

    return [
      {
        id: VISIBLE,
        value: VISIBLE,
        label: t("classRoom:visible_to_students_now"),
      },
      {
        id: TO_BE_VISIBLE,
        value: TO_BE_VISIBLE,
        label: isFixedStartTime
          ? t("classRoom:visible_to_students_on_start_time")
          : t("classRoom:visible_to_students_on_open_date_and_time"),
        isDisabled: !isOpenDateInFuture,
      },
      {
        id: PRIVATE,
        value: PRIVATE,
        label: t("classRoom:visible_to_teachers_only"),
        isDisabled: isFixedStartTime && !isOpenDateInFuture,
      },
    ];
  },
  JSON.stringify
);

export const getAssignmentCommonSettings = params => {
  const {
    t,
    isTurnitinEnabledForGrade,
    contentType = "ASSESSMENT",
    isExcludedFromFinalGradeCalculationEnabled,
    isQt,
    isWorksheet = false,
    isFromClassMaterials,
    assessmentType,
    isStudentSubmissionEnabled,
    isClassDiscussionEnabled,
    isSimilarityCheckEnabled,
    isExcludedFromFinalGradeCalculation,
    isSharedWithClassMaterial,
    isOnScreenCalculatorEnabled,
    showStudentResponse,
    isIncludedInCourseFlow,
    isCourseFlow,
    learningCourseId,
    curriculumProgramId,
    isDpPa,
    isDpEp,
    courseId,
    primaryGradeId,
    taskTypeGroup,
    hasUnitPlan,
  } = params;

  const isAITutor =
    taskTypeGroup === TASK_TYPE_AI_TUTOR ||
    assessmentType === ASSESSMENT_TYPE_AI_TUTOR;
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
  const isToddleLibrary = isToddleLibraryRoute();

  const isToddleLibrary2_0 = isToddleLibrary && isLearningCoursesEnabled;

  const isUnitTagged2_0 =
    isLearningCoursesEnabled &&
    hasUnitPlan &&
    getIsAssignmentsUnitTaggingIn2_0Enabled();

  const learningCourseContexts = generateRnPContextsByLevel({
    level: "LEARNING_COURSE",
    learningCourseId,
    curriculumProgramId,
    gradeId: primaryGradeId,
  });
  const learningCourseContext = _.first(learningCourseContexts);
  const courseContexts = generateRnPContextsByLevel({
    level: "COURSE",
    courseId,
    curriculumProgramId,
    gradeId: primaryGradeId,
  });
  const courseContext = _.first(courseContexts);

  const showExcludeFromFinalGradeSettingInQuickTask =
    isQt &&
    ACLStore.can("FeatureFlag:ShowExcludeFromFinalGradeSettingInQuickTask");

  const ASSIGNMENT_COMMON_SETTINGS = [
    {
      key: "SUBMISSION",
      value: isStudentSubmissionEnabled,
      label: t("classRoom:submission"),
      onlyForViewMode: true, // to show only in LE instructions
    },
    {
      key: "ADD_TO_COURSE_FLOW",
      valueKey: "isIncludedInCourseFlow",
      label: t("classRoom:add_to_course_flow"),
      tooltip: hasUnitPlan
        ? t("classRoom:assignment_will_be_added_inside_unit_in_course_flow")
        : t("classRoom:assignment_will_get_added_to_end_of_course_flow"),
      testId: "assignmentPostSettings-addToCourseFlow",
      value:
        isToddleLibrary2_0 || isUnitTagged2_0 ? true : isIncludedInCourseFlow,
      showSetting:
        !isDpPa && !isDpEp && isLearningCoursesEnabled && !isCourseFlow,
      disabled:
        isToddleLibrary2_0 || isUnitTagged2_0
          ? true
          : !ACLStorage.getPermissionValueByContext(
              CAN_EDIT_COURSE_FLOW_PERMISSION,
              learningCourseContext
            ),
    },
    {
      key: "CLASS_DISCUSSION",
      valueKey: "isClassDiscussionEnabled",
      label: t("classRoom:class_discussion"),
      tooltip: t("classRoom:enable_student_discussion"),
      testId: "assignmentPostSettings-classDiscussion",
      value: isClassDiscussionEnabled,
      showSetting: true,
      disabled: contentType == "ASSIGNMENT_DISCUSSION",
    },
    {
      key: "EXCLUDE_TASK_FROM_FINAL_GRADE_CALCULATION",
      valueKey: "isExcludedFromFinalGradeCalculation",
      label: t("classRoom:exclude_task_from_final_grade_calculation"),
      tooltip: t("classRoom:exclude_task_from_final_grade_calculation_msg"),
      testId: "assignmentPostSettings-excludeTaskFromFinalGradeCalculation",
      value: isExcludedFromFinalGradeCalculation,
      showSetting:
        isExcludedFromFinalGradeCalculationEnabled &&
        (!isQt || showExcludeFromFinalGradeSettingInQuickTask || isWorksheet) &&
        !isAITutor,
    },
    {
      key: "ADD_TO_CLASS_MATERIALS",
      valueKey: "isSharedWithClassMaterial",
      label: t("classMaterials:add_to_class_materials"),
      tooltip: t("classMaterials:add_to_class_materials_for_selected_classes"),
      testId: "assignmentPostSettings-addToClassMaterials",
      value: isSharedWithClassMaterial,
      showSetting:
        !isDpPa &&
        !isDpEp &&
        !isFromClassMaterials &&
        !isLearningCoursesEnabled &&
        ACLStorage.getPermissionValueByContext(
          CLASS_FLOW_EDIT_PERMISSION,
          courseContext
        ),
    },
    {
      key: "ON_SCREEN_CALCULATOR",
      valueKey: "isOnScreenCalculatorEnabled",
      label: t("worksheet:on_screen_calculator"),
      tooltip: t(
        "worksheet:students_will_have_access_to_the_on_screen_scientific_calculator_while_answering_the_worksheet"
      ),
      testId: "assignmentPostSettings-onScreenCalculator",
      value: isOnScreenCalculatorEnabled,
      showSetting: isWorksheet,
    },
    {
      key: "SHOW_STUDENT_RESPONSE",
      valueKey: "showStudentResponse",
      label: t("worksheet:show_student_response"),
      testId: "assignmentPostSettings-showStudentResponse",
      value: showStudentResponse,
      showSetting: isWorksheet,
    },
    {
      key: "CHECK_FOR_SIMILARITY",
      valueKey: "isSimilarityCheckEnabled",
      label: t("classRoom:check_for_similarity"),
      tooltip: t("classRoom:enable_similarity_check_subtext"),
      testId: "assignmentPostSettings-similarityCheck",
      value: isSimilarityCheckEnabled,
      showSetting:
        isTurnitinEnabledForGrade && isStudentSubmissionEnabled && !isWorksheet,
    },
  ];

  return _.filter(
    ASSIGNMENT_COMMON_SETTINGS,
    ({ showSetting = true }) => showSetting
  );
};

const filterForPermissionUtil = object => {
  return _.filter(object, item => _.get(item, "perm") || !_.has(item, "perm"));
};

export const getWorksheetSettings = params => {
  const {
    t,
    updateSelectedItems,
    isWorksheet = false,
    isTimedTestEnabled,
    isLockScreenEnabled,
    isSafeBrowserEnabled,
    timedTestDuration,
  } = params;

  const { hours = 1, minutes = 0 } = timedTestDuration || {};

  let timeSettings = [
    {
      label: t("worksheet:no_time_constraints"),
      value: UNTIMED,
      key: UNTIMED,
      prefixIcon: <WorksheetClipboardOutlined />,
      perm: isWorksheet,
      description: t("worksheet:no_time_constraints_desc"),
    },
    {
      label: t("worksheet:timed_worksheet_with_flexible_start_time"),
      value: TIMED,
      key: TIMED,
      prefixIcon: <ClockAlarmOutlined />,
      perm: isWorksheet,
      description: t("worksheet:timed_worksheet_with_flexible_start_time_desc"),
    },
    {
      label: t("worksheet:timed_worksheet_with_fixed_start_time"),
      value: FIXED_TIMED,
      key: FIXED_TIMED,
      prefixIcon: <ClockAlarmOutlined />,
      perm: isWorksheet,
      description: t("worksheet:timed_worksheet_with_fixed_start_time_desc"),
    },
  ];

  let modeSettings = [
    {
      label: t("worksheet:browser_unrestricted"),
      value: UNRESTRICTED,
      key: UNRESTRICTED,
      prefixIcon: <LockOpenOutlined />,
      perm: isWorksheet,
      disabled: false,
    },
    {
      label: t("worksheet:browser_lock"),
      value: BROWSER_LOCK,
      key: BROWSER_LOCK,
      prefixIcon: <LockOutlined />,
      tooltip: t("classRoom:enable_locked_mode"),
      perm: isWorksheet,
      disabled: true,
    },
    {
      label: t("worksheet:lockdown_application"),
      value: LOCKDOWN_BROWSER,
      key: LOCKDOWN_BROWSER,
      prefixIcon: <PolicyOutlined />,
      tooltip: t("worksheet:lockdown_tooltip_text"),
      perm: isWorksheet,
      disabled: true,
    },
  ];

  let timeDuration = [
    {
      key: "timed_test",
      label: t("worksheet:test_duration"),
      value: isTimedTestEnabled,
      updateInputField: e => {
        const value = e?.target?.checked;
        updateSelectedItems({
          isTimedTestEnabled: value,
          isLockScreenEnabled: !value ? false : isLockScreenEnabled,
          isSafeBrowserEnabled: !value ? false : isSafeBrowserEnabled,
          timedTestDuration: !value
            ? { hours: 1, minutes: 0 }
            : timedTestDuration,
        });
      },
      showDurationDropdown: true,
      disabled: !isTimedTestEnabled,
      hours,
      minutes,
      durationMenus: [
        {
          key: "hours",
          label: t("common:hours"),
          options: _.range(0, 24),
          value: hours,
          updateInputField: updatedHours => {
            updateSelectedItems({
              timedTestDuration: {
                hours: updatedHours,
                minutes: updatedHours === 0 ? 5 : minutes,
              },
            });
          },
        },
        {
          key: "minutes",
          label: t("common:minutes"),
          options: hours === 0 ? _.range(5, 60, 5) : _.range(0, 60, 5),
          value: minutes,
          updateInputField: updatedMinutes => {
            updateSelectedItems({
              timedTestDuration: {
                ...timedTestDuration,
                minutes: updatedMinutes,
              },
            });
          },
        },
      ],
      perm: isWorksheet,
      testId: "assignmentPostSettings-timedTest",
    },
  ];

  timeSettings = filterForPermissionUtil(timeSettings);
  modeSettings = filterForPermissionUtil(modeSettings);
  timeDuration = filterForPermissionUtil(timeDuration);

  return {
    timeSettings,
    modeSettings,
    timeDuration,
  };
};

export const getAssessmentData = _.memoize(assessmentDetails => {
  const allFields = _.get(assessmentDetails, "allFields");
  const assessmentType = _.get(assessmentDetails, "assessmentType.value");

  const title = _.find(allFields, { uid: "title" })?.value || "";

  const categoryDetails = _.get(assessmentDetails, "category", {}) || {};
  const { label: scoreCategoryLabel, set } = categoryDetails || {};
  const courseIdsOfScoreCategorySet = getCourseIdsMemoize({
    associatedEntities: _.get(set, "associatedEntities", []),
    entityType: "Grade",
  });

  const subjectsField = _.find(
    allFields,
    field => _.get(field, "uid") === "subjects"
  );
  const subjectName = _.get(
    subjectsField,
    "resolvedMinimalTree.subjects[0].name",
    ""
  );

  const isQt = _.isEqual(
    _.get(assessmentDetails, "type"),
    ASSESSMENT_TYPE_QUICK_TASK
  );
  const isWorksheet = _.includes(
    WORKSHEET_TEMPLATES,
    _.get(assessmentDetails, "templateId", "")
  );
  const assessmentTypeValue = _.get(
    assessmentDetails,
    "assessmentType.value",
    ""
  );
  const isDpEp = _.isEqual(assessmentTypeValue, "dp_ep");
  const isDpPa = _.isEqual(assessmentTypeValue, "dp_pa");

  const assignmentTags = _.get(
    _.find(allFields, field => {
      return _.get(field, "uid") === "subjectLevel";
    }),
    "value"
  );

  const unitPlanField = _.find(allFields, { uid: "unitPlan" });
  const hasUnitPlan = !_.isEmpty(
    _.get(unitPlanField, "resolvedMinimalTree.unitPlan")
  );

  return {
    title,
    isQt,
    isWorksheet,
    assignmentTags,
    subjectsField,
    scoreCategoryLabel,
    subjectName,
    courseIdsOfScoreCategorySet,
    isDpEp,
    isDpPa,
    assessmentType,
    hasUnitPlan,
  };
}, JSON.stringify);

export const getGenericFolders = _.memoize(data => {
  const courseEdges = _.get(data, "coursesV2.edges", []);
  const courses = _.map(courseEdges, "node");
  return _.reduce(
    courses,
    (result, { id, genericFolders }) => {
      result = result.concat(
        _.map(genericFolders, ({ id: folderId, name, color }) => {
          return {
            id: folderId,
            label: name,
            value: folderId,
            color: color,
            courseId: id,
          };
        })
      );
      return result;
    },
    []
  );
}, JSON.stringify);

export const updatedClassItem = ({ id, params, courses }) => {
  const index = _.findIndex(courses, { id });
  return update(courses, {
    [index]: { $set: { ...courses[index], ...params } },
  });
};

export const getTagOptions = params => {
  const { courseId, genericFolders } = params;

  const currentCourseFolders = _.filter(genericFolders, { courseId });

  const genericFoldersWithIcons = _.map(
    currentCourseFolders,
    ({ color, value, ...rest }) => {
      return {
        ...rest,
        id: value,
        value,
        icon: <CircleFilled style={{ color }} />,
      };
    }
  );

  return genericFoldersWithIcons;
};

export const getMomentFromDateAndTimeString = ({
  dateTimeString,
  dateString,
  timeString,
}) => {
  if (!_.isEmpty(dateTimeString)) {
    return moment(dateTimeString, "YYYY-MM-DD:HH:mm");
  }
  if (!_.isEmpty(dateString) && !_.isEmpty(timeString)) {
    return moment(`${dateString}:${timeString}`, "YYYY-MM-DD:HH:mm");
  }
  if (!_.isEmpty(dateString)) {
    return moment(dateString, "YYYY-MM-DD");
  }
  if (!_.isEmpty(timeString)) {
    return moment(timeString, "HH:mm");
  }
  return null;
};

const getDateTokens = ({ date1, date2 = null }) => {
  if (_.isEmpty(date2)) {
    return {
      date: formatDate({ date: date1, format: DATE_FORMAT }),
      time: formatDate({ date: date1, format: TIME_FORMAT }),
    };
  }

  return {
    date1: formatDate({ date: date1, format: DATE_FORMAT }),
    time1: formatDate({ date: date1, format: TIME_FORMAT }),
    date2: formatDate({ date: date2, format: DATE_FORMAT }),
    time2: formatDate({ date: date2, format: TIME_FORMAT }),
  };
};

export const getClassSettingsFooterText = ({
  visibility,
  opensOn,
  dueOn: initialDueOn,
  closesOn: initialClosesOn,
  isDueDateBeforeOpenDate,
  isCloseDateBeforeDueDate,
  isCloseDateBeforeOpenDate,
  t,
  isFixedStartTime,
  timedTestDuration,
}) => {
  // When there is error in date selection, consider that date field as empty for showing correct text in footer
  let dueOn = initialDueOn;
  let closesOn = initialClosesOn;

  if (isFixedStartTime) {
    // For fixed start time, we will not show due date, closes date will be calculated based on opensOn and timedTestDuration
    dueOn = null;
    closesOn = opensOn
      ? moment(opensOn)
          .add(timedTestDuration.hours, "hours")
          .add(timedTestDuration.minutes, "minutes")
          .format("YYYY-MM-DD:HH:mm")
      : moment()
          .add(timedTestDuration.hours, "hours")
          .add(timedTestDuration.minutes, "minutes")
          .format("YYYY-MM-DD:HH:mm");
  }

  if (isDueDateBeforeOpenDate) {
    dueOn = null;
  }
  if (isCloseDateBeforeDueDate || isCloseDateBeforeOpenDate) {
    closesOn = null;
  }

  switch (visibility) {
    case TO_BE_VISIBLE: {
      let message = t(
        "classRoom:assignment_will_be_visible_on_date",
        getDateTokens({ date1: opensOn })
      );
      if (!_.isEmpty(closesOn)) {
        message = `${message} ${t(
          "classRoom:submissions_open_on_date_and_close_on_date",
          getDateTokens({ date1: opensOn, date2: closesOn })
        )}`;

        const isLateSubmissionsNotAllowed =
          _.isEmpty(dueOn) ||
          checkIsDateSame({
            date1: dueOn,
            date2: closesOn,
            check: "minute",
          });

        if (isLateSubmissionsNotAllowed) {
          message = `${message} ${t(
            "classRoom:late_submissions_are_not_allowed"
          )}`;
        } else {
          message = `${message} ${t(
            "classRoom:submissions_made_after_date_will_be_considered_late",
            getDateTokens({ date1: dueOn })
          )}`;
        }
      } else if (!_.isEmpty(dueOn)) {
        message = `${message} ${t(
          "classRoom:submissions_open_on_date_and_are_due_by_date",
          getDateTokens({ date1: opensOn, date2: dueOn })
        )}  ${t("classRoom:late_submissions_are_allowed")}`;
      } else {
        message = `${message} ${t(
          "classRoom:submissions_open_on_date_with_no_deadline",
          getDateTokens({ date1: opensOn })
        )}`;
      }
      return message;
    }
    case VISIBLE: {
      let message = t("classRoom:assignment_immediately_visible_to_students");

      const isOpensOnNowOrPastDate =
        _.isEmpty(opensOn) ||
        checkIsDateBefore({ date1: opensOn, date2: moment(), key: "minutes" });

      if (isOpensOnNowOrPastDate) {
        if (!_.isEmpty(closesOn)) {
          message = `${message} ${t(
            "classRoom:submissions_open_immediately_and_close_on_date",
            getDateTokens({ date1: closesOn })
          )}`;

          const isLateSubmissionsNotAllowed =
            _.isEmpty(dueOn) ||
            checkIsDateSame({
              date1: dueOn,
              date2: closesOn,
              check: "minute",
            });

          if (isLateSubmissionsNotAllowed) {
            message = `${message} ${t(
              "classRoom:late_submissions_are_not_allowed"
            )}`;
          } else {
            message = `${message} ${t(
              "classRoom:submissions_made_after_date_will_be_considered_late",
              getDateTokens({ date1: dueOn })
            )}`;
          }
        } else if (!_.isEmpty(dueOn)) {
          message = `${message} ${t(
            "classRoom:submissions_are_due_by_date",
            getDateTokens({ date1: dueOn })
          )} ${t("classRoom:late_submissions_are_allowed")}`;
        } else {
          return t(
            "classRoom:assignment_immediately_visible_to_students_and_has_no_deadline"
          );
        }
      } else {
        if (!_.isEmpty(closesOn)) {
          message = `${message} ${t(
            "classRoom:submissions_open_on_date_and_close_on_date",
            getDateTokens({ date1: opensOn, date2: closesOn })
          )}`;

          const isLateSubmissionsNotAllowed =
            _.isEmpty(dueOn) ||
            checkIsDateSame({
              date1: dueOn,
              date2: closesOn,
              check: "minute",
            });

          if (isLateSubmissionsNotAllowed) {
            message = `${message} ${t(
              "classRoom:late_submissions_are_not_allowed"
            )}`;
          } else {
            message = `${message} ${t(
              "classRoom:submissions_made_after_date_will_be_considered_late",
              getDateTokens({ date1: dueOn })
            )}`;
          }
        } else if (!_.isEmpty(dueOn)) {
          message = `${message} ${t(
            "classRoom:submissions_open_on_date_and_are_due_by_date",
            getDateTokens({ date1: opensOn, date2: dueOn })
          )} ${t("classRoom:late_submissions_are_allowed")}`;
        } else {
          message = `${message} ${t(
            "classRoom:submissions_open_on_date_with_no_deadline",
            getDateTokens({ date1: opensOn })
          )}`;
        }
      }

      return message;
    }

    default:
      return t("classRoom:assignment_visible_to_teachers_only");
  }
};

export const checkIsOpenDateBeforeCurrentDate = ({ opensOn }) => {
  return (
    !_.isEmpty(opensOn) &&
    checkIsDateBefore({
      date1: opensOn,
      date2: moment(),
      key: "minutes",
    })
  );
};

export const checkIsDueDateBeforeOpenDate = ({ dueOn, opensOn }) => {
  const opensOnToCheck = !_.isEmpty(opensOn) ? opensOn : moment();
  return (
    !_.isEmpty(dueOn) &&
    checkIsDateBefore({
      date1: dueOn,
      date2: opensOnToCheck,
      key: "minutes",
    })
  );
};

export const checkIsCloseDateBeforeDueDate = ({ closesOn, dueOn }) => {
  return (
    !_.isEmpty(dueOn) &&
    !_.isEmpty(closesOn) &&
    checkIsDateBefore({ date1: closesOn, date2: dueOn, key: "minutes" })
  );
};

export const checkIsCloseDateBeforeOpenDate = ({ closesOn, opensOn }) => {
  return (
    !_.isEmpty(opensOn) &&
    !_.isEmpty(closesOn) &&
    checkIsDateBefore({
      date1: closesOn,
      date2: opensOn,
      key: "minutes",
    })
  );
};

export const checkIsCloseDateBeforeCurrentDate = ({
  isDraftMode,
  closesOnToConsider,
  previousClosesOn,
}) => {
  if (_.isEmpty(closesOnToConsider)) {
    return false;
  }

  // In draft mode, if user selects a close date in the past, show error
  // In published assignment edit mode, if previous close date is empty and user selects a close date in the past, show error
  // In published assignment edit mode, if previous close date is not empty and is in future and user selects a close date in the past, show error
  return isDraftMode ||
    _.isEmpty(previousClosesOn) ||
    !checkIsDateBefore({
      date1: previousClosesOn,
      date2: moment(),
      key: "minutes",
    })
    ? checkIsDateBefore({
        date1: closesOnToConsider,
        date2: moment(),
        key: "minutes",
      })
    : false;
};

export const checkIfSelectedDatesNotInRange = ({
  courses,
  isDraftMode,
  setToastMsg,
  currentTimelineSettings = [],
  assignmentData,
}) => {
  let toastMsg = "";
  const oldCourses = _.get(assignmentData, "courses", []);

  const isError = _.some(courses, course => {
    const {
      state: { scheduleDate, scheduleTime } = {},
      deadlineDate,
      deadlineTime,
      closeSubmissionDate,
    } = course;
    const oldCourseData = _.find(oldCourses, { id: course.id }) || {};

    const {
      state: {
        scheduleDate: oldScheduleDate,
        scheduleTime: oldScheduleTime,
      } = {},
      closeSubmissionDate: previousCloseSubmissionDate,
    } = oldCourseData || {};

    const previousOpensOn = getMomentFromDateAndTimeString({
      dateString: oldScheduleDate,
      timeString: oldScheduleTime,
    });
    const opensOn = getMomentFromDateAndTimeString({
      dateString: scheduleDate,
      timeString: scheduleTime,
    });
    const dueOn = getMomentFromDateAndTimeString({
      dateString: deadlineDate,
      timeString: deadlineTime,
    });
    const closesOn = getMomentFromDateAndTimeString({
      dateTimeString: closeSubmissionDate,
    });
    const previousClosesOn = getMomentFromDateAndTimeString({
      dateTimeString: previousCloseSubmissionDate,
    });
    const dueOnToConsider = getDueOnBasedOnTimelineSettings({
      dueOn,
      currentTimelineSettings,
    });
    const isDueDateInPast = checkIsDateBefore({
      date1: dueOnToConsider,
      date2: moment(),
      key: "minutes",
    });
    const opensOnToConsider = getOpensOnBasedOnTimelineSettings({
      opensOn,
      previousOpensOn,
      currentTimelineSettings,
      isDueDateInPast,
    });

    const closesOnToConsider = getClosesOnBasedOnTimelineSettings({
      closesOn,
      currentTimelineSettings,
    });

    const isDueDateBeforeOpenDate = checkIsDueDateBeforeOpenDate({
      dueOn: dueOnToConsider,
      opensOn: opensOnToConsider,
      isDraftMode,
    });
    const isCloseDateBeforeDueDate = checkIsCloseDateBeforeDueDate({
      closesOn: closesOnToConsider,
      dueOn: dueOnToConsider,
    });
    const isCloseDateBeforeOpenDate = checkIsCloseDateBeforeOpenDate({
      closesOn: closesOnToConsider,
      opensOn: opensOnToConsider,
      isDraftMode,
    });
    const isCloseDateBeforeCurrentDate = checkIsCloseDateBeforeCurrentDate({
      isDraftMode,
      closesOnToConsider,
      previousClosesOn,
    });
    const isOpensOnNowOrPastDate =
      _.isEmpty(opensOnToConsider) ||
      checkIsDateBefore({
        date1: opensOnToConsider,
        date2: moment(),
        key: "minutes",
      });

    if (isDueDateBeforeOpenDate) {
      toastMsg = "due_date_cannot_be_before_open_date";
    } else if (isCloseDateBeforeDueDate) {
      toastMsg = "close_date_cannot_be_before_due_date";
    } else {
      toastMsg =
        isOpensOnNowOrPastDate || isCloseDateBeforeCurrentDate
          ? "close_time_cannot_be_in_the_past"
          : "close_date_cannot_be_before_open_date";
    }

    return (
      isDueDateBeforeOpenDate ||
      isCloseDateBeforeDueDate ||
      isCloseDateBeforeOpenDate ||
      isCloseDateBeforeCurrentDate
    );
  });

  if (isError && !_.isEmpty(toastMsg)) {
    setToastMsg({
      msg: `classRoom:${toastMsg}`,
      type: "error",
      isClosable: true,
    });
  }

  return isError;
};

export const shouldPrefillOpenDateFromCourse = course => {
  if (_.isEmpty(course)) {
    return false;
  }

  const { state: { scheduleDate, scheduleTime } = {} } = course;

  const opensOn = getMomentFromDateAndTimeString({
    dateString: scheduleDate,
    timeString: scheduleTime,
  });

  return (
    !_.isEmpty(opensOn) &&
    checkIsDateAfter({ date1: opensOn, date2: moment(), key: "minutes" })
  );
};

export const shouldPrefillDueDateFromCourse = course => {
  if (_.isEmpty(course)) {
    return false;
  }

  const { deadlineDate, deadlineTime } = course;

  const dueOn = getMomentFromDateAndTimeString({
    dateString: deadlineDate,
    timeString: deadlineTime,
  });

  return (
    !_.isEmpty(dueOn) &&
    checkIsDateAfter({ date1: dueOn, date2: moment(), key: "minutes" })
  );
};

export const shouldPrefillCloseDateFromCourse = course => {
  if (_.isEmpty(course)) {
    return false;
  }

  const { closeSubmissionDate } = course;

  const closesOn = getMomentFromDateAndTimeString({
    dateTimeString: closeSubmissionDate,
  });

  return (
    !_.isEmpty(closesOn) &&
    checkIsDateAfter({ date1: closesOn, date2: moment(), key: "minutes" })
  );
};

export const getPrefilledValuesFromDefaultCourse = ({
  newCourses,
  defaultCourse,
}) => {
  if (_.isEmpty(defaultCourse)) {
    return newCourses;
  }

  const {
    state = {},
    closeSubmissionDate,
    deadlineDate,
    deadlineTime,
    visibility = VISIBLE,
    isLockScreenEnabled,
    isSafeBrowserEnabled,
    timedTestDuration,
  } = defaultCourse;

  const opensOn = getMomentFromDateAndTimeString({
    dateString: state?.scheduleDate,
    timeString: state?.scheduleTime,
  });

  const shouldPrefillDueDate = shouldPrefillDueDateFromCourse(defaultCourse);
  const shouldPrefillCloseDate = shouldPrefillCloseDateFromCourse(
    defaultCourse
  );
  const shouldPrefillOpenDate = shouldPrefillOpenDateFromCourse(defaultCourse);

  return _.map(newCourses, course => {
    return {
      ...course,
      state: {
        ...state,
        scheduleDate: shouldPrefillOpenDate ? state?.scheduleDate : null,
        scheduleTime: shouldPrefillOpenDate ? state?.scheduleTime : null,
      },
      closeSubmissionDate: shouldPrefillCloseDate ? closeSubmissionDate : null,
      deadlineDate: shouldPrefillDueDate ? deadlineDate : null,
      deadlineTime: shouldPrefillDueDate ? deadlineTime : null,
      visibility:
        visibility === TO_BE_VISIBLE &&
        checkIsDateBefore({ date1: opensOn, date2: moment(), key: "minutes" })
          ? VISIBLE
          : visibility,
      ...(getIsWorksheetAssignModalV2Enabled()
        ? {
            isLockScreenEnabled: isLockScreenEnabled || false,
            isSafeBrowserEnabled: isSafeBrowserEnabled || false,
            timedTestDuration: timedTestDuration || { hours: 1, minutes: 0 },
          }
        : {}),
    };
  });
};

export const getOpenDateRange = ({
  academicYearStartDate,
  academicYearEndDate,
  allowBackDate,
  preventFutureOpenDate,
  initialTimeMoment,
  opensOn,
}) => {
  return {
    minDate: allowBackDate ? moment(academicYearStartDate) : moment(),
    maxDate: preventFutureOpenDate ? moment() : moment(academicYearEndDate),
    minTime: allowBackDate
      ? null
      : getIsCurrentDateSelected({ date: opensOn })
      ? moment(initialTimeMoment, "HH:mm")
      : null,
    maxTime:
      preventFutureOpenDate && getIsCurrentDateSelected({ date: opensOn })
        ? moment(initialTimeMoment.subtract(15, "minute"), "HH:mm")
        : null,
  };
};

export const getDueDateRange = ({
  academicYearEndDate,
  academicYearStartDate,
  initialTimeMoment,
  opensOn,
  dueOn,
}) => {
  let minTimeMoment = null;
  if (
    !_.isEmpty(opensOn) &&
    checkIsDateSame({ date1: opensOn, date2: dueOn })
  ) {
    const { time: minTime } = getScheduleOrDeadlineTime({
      defaultStartTime: moment(opensOn).format("HH:mm"),
    });
    minTimeMoment = getMomentFromDateAndTimeString({
      timeString: minTime,
    });
  }

  return {
    minDate: moment(academicYearStartDate),
    maxDate: moment(academicYearEndDate),
    minTime: !_.isEmpty(minTimeMoment)
      ? minTimeMoment
      : getIsCurrentDateSelected({ date: dueOn })
      ? initialTimeMoment
      : null,
    maxTime: null,
  };
};

export const getCloseDateRange = ({
  academicYearEndDate,
  initialTimeMoment,
  opensOn,
  dueOn,
  closesOn,
}) => {
  if (!_.isEmpty(dueOn)) {
    let minTimeMoment = null;
    if (checkIsDateSame({ date1: dueOn, date2: closesOn })) {
      const { time: minTime } = getScheduleOrDeadlineTime({
        defaultStartTime: moment(dueOn).format("HH:mm"),
      });
      minTimeMoment = getMomentFromDateAndTimeString({
        timeString: minTime,
      });
    }

    const isDueDateInPast = checkIsDateBefore({
      date1: dueOn,
      date2: moment(),
      key: "minutes",
    });

    return {
      minDate: isDueDateInPast ? moment() : moment(dueOn),
      maxDate: moment(academicYearEndDate),
      minTime: !_.isEmpty(minTimeMoment)
        ? minTimeMoment
        : getIsCurrentDateSelected({ date: closesOn })
        ? initialTimeMoment
        : null,
      maxTime: null,
    };
  }

  let minTimeMoment = null;
  if (
    !_.isEmpty(opensOn) &&
    checkIsDateSame({ date1: opensOn, date2: closesOn })
  ) {
    const { time: minTime } = getScheduleOrDeadlineTime({
      defaultStartTime: moment(opensOn).format("HH:mm"),
    });
    minTimeMoment = getMomentFromDateAndTimeString({
      timeString: minTime,
    });
  }
  return {
    minDate:
      !_.isEmpty(opensOn) &&
      !checkIsDateBefore({
        date1: opensOn,
        date2: moment(),
        key: "minutes",
      })
        ? moment(opensOn)
        : moment(),
    maxDate: moment(academicYearEndDate),
    minTime: !_.isEmpty(minTimeMoment)
      ? minTimeMoment
      : getIsCurrentDateSelected({ date: closesOn })
      ? initialTimeMoment
      : null,
    maxTime: null,
  };
};

export const getOpensOnBasedOnTimelineSettings = ({
  opensOn,
  previousOpensOn,
  currentTimelineSettings = [],
  isDueDateInPast = false,
}) => {
  if (_.isEmpty(opensOn)) {
    return null;
  }

  const isScheduleDateUpdated =
    _.isEmpty(previousOpensOn) ||
    !checkIsDateSame({
      date1: opensOn,
      date2: previousOpensOn,
      check: "minutes",
    });

  if (
    !_.includes(currentTimelineSettings, OPENS_ON) &&
    isScheduleDateUpdated &&
    !isDueDateInPast
  ) {
    return null;
  }

  return opensOn;
};

export const getDueOnBasedOnTimelineSettings = ({
  dueOn,
  currentTimelineSettings = [],
}) => {
  if (!_.isEmpty(dueOn) && _.includes(currentTimelineSettings, DUE_ON)) {
    return dueOn;
  }
  return null;
};

export const getClosesOnBasedOnTimelineSettings = ({
  closesOn,
  currentTimelineSettings = [],
}) => {
  if (!_.isEmpty(closesOn) && _.includes(currentTimelineSettings, CLOSES_ON)) {
    return closesOn;
  }
  return null;
};

export const getShowResponseSettingsOptions = ({ t }) => {
  return [
    {
      key: "ALWAYS",
      label: t("worksheet:always_visible"),
      subText: t("worksheet:show_student_response_always"),
    },
    {
      key: "ONCE_EVALUATED",
      label: t("worksheet:once_evaluation_is_shared"),
      subText: t("worksheet:show_student_response_once_evaluated"),
    },
    {
      key: "NEVER",
      label: t("worksheet:never"),
      subText: t("worksheet:show_student_response_never"),
    },
  ];
};

export const getUpdatedCoursesWithDefaultDueDate = ({
  courses,
  leId,
  academicYearId,
  forceUpdate = false,
}) => {
  if (_.isEmpty(courses) || _.isEmpty(leId)) {
    return null;
  }

  const assessmentDetails = getAssessmentDetailsFromCache({
    id: leId,
    collaboratorsFilter: { userAcademicYearId: academicYearId },
  });

  if (!assessmentDetails) {
    return null;
  }

  const allFields = _.get(assessmentDetails, "allFields", []);
  const studentTemplateField = _.find(allFields, { uid: "studentTemplate" });
  const attachmentGroups = _.get(
    studentTemplateField,
    "resolvedMinimalTree.attachmentGroups",
    []
  );

  if (_.isEmpty(attachmentGroups)) {
    return null;
  }

  const ltiToolGroup = _.find(attachmentGroups, group => {
    const attachment = _.first(_.get(group, "attachments", []));
    const attachmentType = _.get(attachment, "metadata.type");
    const attachmentService = _.get(attachment, "metadata.service");
    return attachmentType === "LTI_TOOL" || attachmentService === "TURNITIN";
  });

  if (!ltiToolGroup) {
    return null;
  }

  const firstAttachment = _.first(ltiToolGroup.attachments);
  const metadata = _.get(firstAttachment, "metadata", {});
  const customMetadata = _.get(metadata, "custom") || {};

  const defaultDueDate = customMetadata.default_duedate || metadata.endDateTime;
  const defaultStartDate =
    customMetadata.default_startdate || metadata.startDateTime;

  if (!defaultDueDate && !defaultStartDate) {
    return null;
  }

  const dueDateMoment = defaultDueDate ? moment(defaultDueDate) : null;
  const startDateMoment = defaultStartDate ? moment(defaultStartDate) : null;

  if (
    (dueDateMoment && !dueDateMoment.isValid()) ||
    (startDateMoment && !startDateMoment.isValid())
  ) {
    return null;
  }

  if (!forceUpdate) {
    const hasExistingDates = _.some(
      courses,
      course =>
        !_.isEmpty(course.deadlineDate) || _.get(course, "state.scheduleDate")
    );

    if (hasExistingDates) {
      return null;
    }
  }

  const updatedCourses = _.map(courses, course => ({
    ...course,
    ...(dueDateMoment && {
      deadlineDate: dueDateMoment.format("YYYY-MM-DD"),
      deadlineTime: dueDateMoment.format("HH:mm"),
    }),
    ...(startDateMoment && {
      state: {
        ...course.state,
        scheduleDate: startDateMoment.format("YYYY-MM-DD"),
        scheduleTime: startDateMoment.format("HH:mm"),
      },
    }),
  }));

  return {
    updatedCourses,
    shouldAddDueOnSetting: !!defaultDueDate,
    shouldAddOpensOnSetting: !!defaultStartDate,
  };
};

export const checkHasLtiAttachment = ({ leId, academicYearId }) => {
  if (_.isEmpty(leId)) {
    return false;
  }

  const assessmentDetails = getAssessmentDetailsFromCache({
    id: leId,
    collaboratorsFilter: { userAcademicYearId: academicYearId },
  });

  if (!assessmentDetails) {
    return false;
  }

  const allFields = _.get(assessmentDetails, "allFields", []);
  const studentTemplateField = _.find(allFields, { uid: "studentTemplate" });
  const attachmentGroups = _.get(
    studentTemplateField,
    "resolvedMinimalTree.attachmentGroups",
    []
  );

  if (_.isEmpty(attachmentGroups)) {
    return false;
  }

  const hasLti = _.some(attachmentGroups, group => {
    const attachment = _.first(_.get(group, "attachments", []));
    const attachmentType = _.get(attachment, "metadata.type");
    const attachmentService = _.get(attachment, "metadata.service");
    return attachmentType === "LTI_TOOL" || attachmentService === "TURNITIN";
  });

  return hasLti;
};

export const extractGradeIds = grades => {
  return _.compact(
    _.map(grades, grade => {
      // If grade is a string, it's already an ID
      if (typeof grade === "string") {
        return grade;
      }
      // If grade is an object, extract the id property
      return grade?.id;
    })
  );
};

export const isTurnitinEnabledForGrade = (grades, turnitinEnabledGrades) => {
  const gradeIds = extractGradeIds(grades);
  return !_.isEmpty(_.intersection(gradeIds, turnitinEnabledGrades));
};
